import React, { useEffect, useMemo, useState } from 'react';
import CommonLayout from './CommonLayout';
import { useUserSessionStore } from '../../stores/UserSession.store';
import moment from 'moment';
import { orderBy } from 'lodash';
import { BadgeOrder } from '../../components/OrderStatus/BadgeOrder';
import Label from '../../components/Label/Label';
import Input from '../../shared/Input/Input';
import { OrderStatusEnum } from '../../enums/OrderStatus.enum';
import { OrderStatus } from '../../@core/constants/OrderStatus.constant';
import OrderChat from '../../components/OrderChat/OrderChat';
import ButtonPrimary from '../../shared/Button/ButtonPrimary';
import { useReservations } from '../../hooks/collections/useReservations';
import { IReservation } from '../../@core/interfaces/IReservation';

interface Props {
}

const AccountCompanyChats: React.FC<Props> = () => {
  const user = useUserSessionStore((state) => state.user);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<OrderStatusEnum | 'all'>('all');
  const [selectedOrder, setSelectedOrder] = useState<IReservation>(null);
  const { reservations } = useReservations({
    loadData: true,
    queries: [
      { type: 'where', field: 'newMessages', operator: '==', value: true },
      { type: 'where', field: 'service.company.id', operator: '==', value: user?.id }
    ]
  });

  const ordersByCompany = useMemo(() => {
    return reservations
      .filter((reservation) => status !== 'all' ? reservation?.paymentStatus.toString() === status.toString() : true)
      .filter((reservation) => search !== '' ? reservation?.orderId.toLowerCase().includes(search.toLowerCase()) : true);
  }, [reservations, status, search]);

  useEffect(() => {
    if (ordersByCompany.length > 0 && !selectedOrder) setSelectedOrder(ordersByCompany[0]);
  }, [ordersByCompany, selectedOrder]);

  const renderOrdersList = () => {
    return (
      <div className="h-full flex flex-col">
        <div className="mb-4 flex-shrink-0">
          <div className="flex flex-col space-y-2">
            <div>
              <Label>Search Order ID</Label>
              <Input
                onChange={(e) => setSearch(e.target.value)}
                className="mt-1.5"
                placeholder="Search by order ID"
              />
            </div>
            <div>
              <Label>Status</Label>
              <select
                onChange={(e) => e.target.value === 'all' ? setStatus('all') : setStatus(e.target.value as unknown as OrderStatusEnum)}
                className="mt-1.5 w-full h-10 border border-neutral-200 dark:border-neutral-700 rounded-lg"
              >
                <option value="all">All</option>
                <option value={OrderStatusEnum.PAID}>{OrderStatus[OrderStatusEnum.PAID].label}</option>
                <option value={OrderStatusEnum.CANCELLED}>{OrderStatus[OrderStatusEnum.CANCELLED].label}</option>
                <option value={OrderStatusEnum.GENERATED}>{OrderStatus[OrderStatusEnum.GENERATED].label}</option>
              </select>
            </div>
          </div>
        </div>

        <div className="flex-grow overflow-y-auto border border-neutral-200 dark:border-neutral-700 rounded-lg">
          {ordersByCompany.length > 0 ? (
            <div className="divide-y divide-neutral-200 dark:divide-neutral-700">
              {orderBy(ordersByCompany, ['createdAt'], ['desc']).map((order: IReservation) => (
                <div
                  key={order.id}
                  className={`p-4 cursor-pointer hover:bg-neutral-100 dark:hover:bg-neutral-800 ${
                    selectedOrder?.id === order.id ? 'bg-neutral-100 dark:bg-neutral-800' : ''
                  }`}
                  onClick={() => setSelectedOrder(order)}
                >
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-medium">{order.orderId}</span>
                    <BadgeOrder status={order.paymentStatus}/>
                  </div>
                  <div className="text-sm text-neutral-500 dark:text-neutral-400 mb-1">
                    {order.service.title}
                  </div>
                  <div className="text-xs text-neutral-500 dark:text-neutral-400">
                    {moment(order.hour, 'HH:mm').format('hh:mm A')}, {moment(order.day, 'YYYY-MM-DD').format('MMM D, YYYY')}
                  </div>
                  <div className="text-xs text-neutral-500 dark:text-neutral-400">
                    Customer: {order.createdBy?.name || 'Unknown'}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex items-center justify-center h-full p-4">
              <p className="text-neutral-500 dark:text-neutral-400">No orders found.</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderChatSection = () => {
    if (!selectedOrder) {
      return (
        <div className="flex items-center justify-center h-full border border-neutral-200 dark:border-neutral-700 rounded-lg">
          <p className="text-neutral-500 dark:text-neutral-400">Select an order to view the chat.</p>
        </div>
      );
    }

    return (
      <div className="h-full flex flex-col">
        <div className="bg-white dark:bg-neutral-900 p-4 border-b border-neutral-200 dark:border-neutral-700 flex-shrink-0">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-medium">{selectedOrder.service.title}</h3>
              <p className="text-sm text-neutral-500 dark:text-neutral-400">
                Order ID: {selectedOrder.orderId} | Status: <BadgeOrder status={selectedOrder.paymentStatus}/>
              </p>
            </div>
            <ButtonPrimary
              className="h-10"
              onClick={() => window.open(`/reservation-detail/${selectedOrder.id}`, '_blank')}
            >
              View Details
            </ButtonPrimary>
          </div>
        </div>

        <div className="flex-grow overflow-hidden">
          {selectedOrder && <OrderChat
            key={selectedOrder.id} reservationId={selectedOrder.id} onResolveChat={() => {
            setSelectedOrder(null);
            setSearch('');
            setStatus('all');
          }}
          />}
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col">
      <CommonLayout>
        <div className="space-y-6 sm:space-y-8 flex flex-col h-full overflow-y-auto">
          <div className="flex-shrink-0">
            <h2 className="text-3xl font-semibold">Company Chats</h2>
            <div className="w-14 border-b border-neutral-200 dark:border-neutral-700 mt-4"></div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 flex-grow overflow-hidden h-auto">
            <div className="lg:col-span-1 overflow-hidden flex flex-col h-96 md:h-[600px]">
              {renderOrdersList()}
            </div>
            <div className="lg:col-span-2 overflow-hidden flex flex-col h-96 md:h-[600px]">
              {renderChatSection()}
            </div>
          </div>
        </div>
      </CommonLayout>
    </div>
  );
};

export default AccountCompanyChats;
